import { useEffect } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Text, UseTooltipProps, useTooltip } from '@frontend/design-system';

type CoachmarkTooltipProps = Omit<UseTooltipProps, 'initialOpen'> & {
  /** Message shown inside the tooltip */
  text: string;
  /** Called once on first render (to increment show count) */
  onShow: () => void;
  /** Called when the user dismisses the tooltip */
  onDismiss: () => void;
  /** CSS override for the dot's position relative to the parent container */
  dotCss?: SerializedStyles;
};

/**
 * Shared coachmark tooltip used across analytics features.
 * Renders a pulsing dot trigger and a dismissible tooltip.
 * Mount/unmount this component conditionally — it records `onShow` once on
 * mount and `onDismiss` when the user clicks the close icon.
 */
export const CoachmarkTooltip = ({
  text,
  onShow,
  onDismiss,
  dotCss,
  placement = 'top-end',
  trigger = 'click',
  theme: tooltipTheme = 'light',
  showArrow = true,
  enableDismiss = false,
  ...tooltipProps
}: CoachmarkTooltipProps) => {
  const {
    Tooltip,
    tooltipProps: floatingProps,
    triggerProps,
    setOpen,
  } = useTooltip({
    placement,
    trigger,
    theme: tooltipTheme,
    showArrow,
    enableDismiss,
    initialOpen: true,
    ...tooltipProps,
  });

  useEffect(() => {
    onShow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDismiss = () => {
    setOpen(false);
    onDismiss();
  };

  return (
    <>
      {floatingProps.isOpen && (
        <span ref={triggerProps.ref} css={[defaultDotStyles, dotCss]}>
          <span />
        </span>
      )}

      <Tooltip
        {...floatingProps}
        hasInteractiveContent
        css={{
          padding: theme.spacing(2),
          borderRadius: theme.borderRadius.medium,
        }}
      >
        <div css={contentStyles}>
          <Text size='medium' weight='light' css={textStyles}>
            {text}
          </Text>
          <Icon name='x-small' size={16} onClick={handleDismiss} css={closeIconStyles} aria-label='Dismiss' />
        </div>
      </Tooltip>
    </>
  );
};

const defaultDotStyles = css`
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: ${theme.borderRadius.full};
  background-color: ${theme.colorsV2.seaweed20()};
  pointer-events: none;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  > span {
    width: 6px;
    height: 6px;
    border-radius: ${theme.borderRadius.full};
    background-color: ${theme.colorsV2.seaweed70()};
    pointer-events: none;
    z-index: 2;
  }
`;

const contentStyles = css`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing(2)};
  max-width: 190px;
`;

const textStyles = css`
  font-weight: normal;
`;

const closeIconStyles = css`
  flex-shrink: 0;
  cursor: pointer;
  margin-top: ${theme.spacing(0.5)};
`;
